import { useState, useEffect, useRef } from 'react';

const COLORS = ['#6366f1', '#06b6d4', '#8b5cf6', '#f59e0b'];
const DESCS = [
  'Fondasi algoritmik yang dibangun untuk hasil yang pasti dan stabilitas yang radikal.',
  'Fungsionalitas yang mengantisipasi kebutuhan bahkan sebelum muncul.',
  'Konektivitas tanpa hambatan di berbagai ekosistem yang terfragmentasi.',
  'Implementasi teliti di mana setiap baris kode berpadu menjadi performa tinggi.',
];
const LUNE = ['L — Logic', 'U — Utility', 'N — Network', 'E — Execution'];

export function LuneScrollFX({ sections }) {
  const containerRef = useRef(null);
  const [idx, setIdx] = useState(0);
  const [isMobile, setMobile] = useState(false);
  const total = sections.length;

  useEffect(() => {
    const check = () => setMobile(window.innerWidth < 768);
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);

  useEffect(() => {
    const onScroll = () => {
      const el = containerRef.current;
      if (!el) return;
      const scrollable = el.offsetHeight - window.innerHeight;
      if (scrollable <= 0) return;
      const scrolled = Math.max(0, -el.getBoundingClientRect().top);
      const newIdx = Math.min(total - 1, Math.floor((scrolled / scrollable) * total));
      setIdx(newIdx);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener('scroll', onScroll);
  }, [total]);

  /* ── MOBILE: simple stacked cards, no scroll trap ── */
  if (isMobile) {
    return (
      <div style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
        {/* Mini header */}
        <div style={{ padding: '2rem 1.25rem 1rem', textAlign: 'center' }}>
          <p style={{ fontSize: '0.55rem', color: 'rgba(255,255,255,0.3)', letterSpacing: '0.25em', fontFamily: "'Space Mono',monospace", textTransform: 'uppercase', margin: '0 0 0.3rem' }}>METODOLOGI</p>
          <p style={{ fontSize: '0.9rem', color: 'rgba(255,255,255,0.8)', fontWeight: 600, letterSpacing: '-0.02em', margin: 0 }}>The L.U.N.E Framework</p>
        </div>

        {sections.map((s, i) => (
          <div key={i} style={{ position: 'relative', height: '60vw', minHeight: '220px', maxHeight: '320px', overflow: 'hidden', marginBottom: '2px' }}>
            <img src={s.background} alt={s.title} loading="lazy" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', filter: 'brightness(0.45)' }} />
            <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.4)' }} />
            {/* Progress line */}
            <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: '2px', background: COLORS[i] }} />
            <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '1.5rem', textAlign: 'center' }}>
              <span style={{ fontSize: '0.55rem', fontFamily: "'Space Mono',monospace", letterSpacing: '0.28em', color: COLORS[i], textTransform: 'uppercase', marginBottom: '0.6rem', display: 'block' }}>{LUNE[i]}</span>
              <h2 style={{ margin: 0, color: '#fff', fontSize: 'clamp(2.8rem,12vw,4.5rem)', fontWeight: 900, letterSpacing: '-0.04em', lineHeight: 0.9, textTransform: 'uppercase' }}>{s.title}</h2>
              <p style={{ marginTop: '0.75rem', fontSize: '0.72rem', color: 'rgba(255,255,255,0.4)', fontFamily: "'Inter',sans-serif", lineHeight: 1.65, maxWidth: '24rem' }}>{DESCS[i]}</p>
            </div>
            {/* Section num */}
            <div style={{ position: 'absolute', top: '0.75rem', right: '0.75rem', fontFamily: "'Space Mono',monospace", fontSize: '0.6rem', color: 'rgba(255,255,255,0.25)' }}>
              <span style={{ color: COLORS[i], fontWeight: 700 }}>0{i + 1}</span> / 0{total}
            </div>
          </div>
        ))}
      </div>
    );
  }

  return (
    <div ref={containerRef} style={{ height: `${total * 100}vh`, position: 'relative' }}>
      <div style={{
        position: 'sticky', top: 0, height: '100vh',
        overflow: 'hidden', background: '#060608',
        fontFamily: "'Space Grotesk', sans-serif",
      }}>

        {/* Backgrounds */}
        {sections.map((s, i) => (
          <img key={i} src={s.background} alt="" style={{
            position: 'absolute', inset: 0, width: '100%', height: '100%',
            objectFit: 'cover', objectPosition: 'center',
            filter: 'brightness(0.42)',
            opacity: i === idx ? 1 : 0,
            transition: 'opacity 0.9s ease',
            willChange: 'opacity',
          }} />
        ))}
        <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.42)' }} />

        {/* Progress bar */}
        <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: '2px', background: 'rgba(255,255,255,0.07)', zIndex: 20 }}>
          <div style={{
            height: '100%', background: COLORS[idx],
            width: `${((idx + 1) / total) * 100}%`,
            transition: 'width 0.6s ease, background 0.6s ease',
          }} />
        </div>

        {/* Top header */}
        <div style={{
          position: 'absolute', top: 0, left: 0, right: 0,
          display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
          padding: 'clamp(1.5rem,4vh,3rem) clamp(1.5rem,5vw,4rem)',
          zIndex: 10,
        }}>
          <div>
            <p style={{ fontSize: '0.58rem', color: 'rgba(255,255,255,0.3)', letterSpacing: '0.25em', fontFamily: "'Space Mono',monospace", textTransform: 'uppercase', margin: 0 }}>METODOLOGI</p>
            <p style={{ fontSize: 'clamp(0.8rem,1.5vw,1rem)', color: 'rgba(255,255,255,0.75)', fontWeight: 600, letterSpacing: '-0.02em', margin: '0.3rem 0 0' }}>The L.U.N.E Framework</p>
          </div>
          <div style={{ fontFamily: "'Space Mono',monospace", fontSize: '0.68rem', color: 'rgba(255,255,255,0.3)', letterSpacing: '0.1em' }}>
            <span style={{ color: COLORS[idx], fontSize: '1rem', fontWeight: 700 }}>0{idx + 1}</span> / 0{total}
          </div>
        </div>

        {/* Section content — crossfade */}
        {sections.map((s, i) => (
          <div key={i} style={{
            position: 'absolute', inset: 0,
            display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
            opacity: i === idx ? 1 : 0,
            transform: i === idx ? 'translateY(0)' : (i > idx ? 'translateY(28px)' : 'translateY(-28px)'),
            transition: 'opacity 0.65s ease, transform 0.65s ease',
            pointerEvents: i === idx ? 'auto' : 'none',
            zIndex: 5,
            padding: isMobile ? '0 1.25rem' : '0 5rem',
            textAlign: 'center',
          }}>
            <span style={{
              fontSize: '0.58rem', fontFamily: "'Space Mono',monospace",
              letterSpacing: '0.28em', color: COLORS[i],
              textTransform: 'uppercase', marginBottom: '1rem', display: 'block',
            }}>{LUNE[i]}</span>

            <h2 style={{
              margin: 0, color: '#fff',
              fontSize: isMobile ? 'clamp(3.5rem,18vw,6rem)' : 'clamp(4.5rem,16vw,12rem)',
              fontWeight: 900, letterSpacing: '-0.04em',
              lineHeight: 0.88, textTransform: 'uppercase',
            }}>{s.title}</h2>

            <p style={{
              marginTop: '1.5rem',
              fontSize: isMobile ? '0.78rem' : 'clamp(0.78rem,1.2vw,0.9rem)',
              color: 'rgba(255,255,255,0.38)', fontFamily: "'Inter',sans-serif",
              maxWidth: '26rem', lineHeight: 1.75,
            }}>{DESCS[i]}</p>
          </div>
        ))}

        {/* Side labels — hidden on mobile */}
        {!isMobile && ['leftLabel', 'rightLabel'].map((key, side) => (
          <div key={key} style={{
            position: 'absolute',
            [side === 0 ? 'left' : 'right']: 'clamp(1rem,3vw,2.5rem)',
            top: '50%', transform: 'translateY(-50%)',
            zIndex: 10,
          }}>
            <p style={{
              fontSize: '0.58rem', fontFamily: "'Space Mono',monospace",
              letterSpacing: '0.22em', color: 'rgba(255,255,255,0.2)',
              textTransform: 'uppercase', margin: 0,
              writingMode: 'vertical-rl',
              transform: side === 0 ? 'rotate(180deg)' : 'none',
              transition: 'color 0.4s ease',
            }}>{sections[idx]?.[key]}</p>
          </div>
        ))}

        {/* Dot indicators */}
        <div style={{
          position: 'absolute', bottom: 'clamp(1.5rem,4vh,2.5rem)',
          left: '50%', transform: 'translateX(-50%)',
          display: 'flex', gap: '8px', zIndex: 10,
        }}>
          {sections.map((_, i) => (
            <div key={i} style={{
              width: i === idx ? '22px' : '5px', height: '4px',
              background: i === idx ? COLORS[i] : 'rgba(255,255,255,0.18)',
              borderRadius: '2px',
              transition: 'all 0.45s ease',
            }} />
          ))}
        </div>

      </div>
    </div>
  );
}
